<template>
    <div class="converter">
        <Uploader
            ref="uploader"
            v-model:value="file"
            purpose="convert"
            placeholder="Add your document here"
            colour="translucent-dark"
            :radius="16"
            :accept="allowedFileTypes.join(',')"
            @update:value="onUpload"
            @change="
                isUploading = true;
                $track.event('Document Upload Initiated', {
                    'document_format': $event[0].type,
                    'acceptable_file_types': allowedFileTypes,
                })
            "
        >
            <template #placeholder>
                <Txt size="xs">
                    Supports standard document formats including  docx, pdf, and pptx.
                </Txt>
            </template>
        </Uploader>
        <Animation
            v-if="animation"
            src="transform-star-1"
            width="120"
            height="120"
        />
        <Animation
            v-if="animation"
            src="transform-star-2"
            width="120"
            height="120"
        />
    </div>
</template>

<script>
import Txt from '@/components/form/txt';
import Uploader from '@/components/form/uploader';
import Animation from '@/components/animation/animation';

export default {
    name: 'Converter',
    components: { Txt, Uploader, Animation },
    inject: ['$global'],
    props: {
        animation: { type: Boolean, default: true }
    },
    emits: ['upload', 'uploading'],
    data() {
        return {
            file: null,
            isUploading: false
        };
    },
    computed: {
        allowedFileTypes() {
            const types = [
                'application/msword',
                'application/vnd.ms-office',
                'application/vnd.ms-excel',
                'application/vnd.ms-powerpoint',
                'application/vnd.ms-word.document.macroEnabled.12',
                'application/vnd.ms-word.template.macroEnabled.12',
                'application/vnd.ms-excel.sheet.macroEnabled.12',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                'application/vnd.openxmlformats-officedocument.presentationml.template',
                'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
                'application/vnd.oasis.opendocument.spreadsheet',
                'application/x-iwork-keynote-sffkey',
                'application/x-iwork-pages-sffpages',
                'application/x-iwork-numbers-sffnumbers',
                'application/vnd.apple.keynote',
                'application/vnd.apple.pages',
                'application/pdf',
                'application/rtf',
                'text/plain',
                'text/rtf',
                'text/csv',
                'application/x-mspublisher',
                'application/x-qw'
            ];

            if (!this.$global.isProduction) {
                const mediaTypes = [
                    // Video
                    'video/mp4',
                    'video/mpeg',
                    'video/quicktime',
                    'video/3gpp',
                    'video/3gpp2',
                    'video/flv',
                    'video/avi',
                    'video/x-flv',
                    'video/x-matroska',
                    'video/x-msvideo',
                    'video/x-ms-wmv',
                    'video/x-ms-asf',
                    'application/x-troff-msvideo',

                    // Audio
                    'audio/mpeg',
                    'audio/mp4',
                    'audio/x-wav',
                    'audio/x-ms-wma',
                    'audio/x-ms-wax',
                    'audio/x-ms-wmv',
                    'audio/x-ms-asf',
                    'audio/x-mpegurl',
                    'audio/x-scpls',
                    'audio/x-mpegurl',
                    'audio/x-pn-realaudio',
                    'audio/x-pn-realaudio-plugin',
                    'audio/x-realaudio',
                ];
                types.push(...mediaTypes);
            }

            return types;
        }
    },
    watch: {
        isUploading(to) {
            if (to) this.$emit('uploading');
        }
    },
    methods: {
        cancel() {
            this.$refs.uploader.cancelUpload();
        },
        onUpload(upload) {
            if (!upload) return;
            this.$emit('upload', upload);
            this.$track.event('Document Uploaded', {
                'document_format': upload.type
            });
        }
    },
};
</script>

<style lang="less" scoped>
.converter {
    position: relative;
    width: 100%;
    height: 320px;
    .uploader {
        width: 100%;
        height: 100%;
    }
    .animation {
        position: absolute;
        top: 0;
        right: 0;
        &:first-of-type {
            top: unset;
            right: unset;
            bottom: 0;
            left: 0;
        }
    }
}
</style>

<style lang="less">
.converter {
    .uploader .target .target-inner {
        min-height: 100%;
    }
}
</style>
